import React, { FC, useContext, useEffect, useState } from 'react';

import {
  ApolloError,
  FetchResult,
  useMutation,
  useQuery,
} from '@apollo/client';
import { BackwardIcon, Button, H2, Preloader } from '@smartshell/ui-kit';
import cn from 'classnames';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { LocalizationContext } from 'localization';
import { ModalDelete } from 'modals';
import {
  CREATE_COMPANIES_NEWS,
  DELETE_COMPANIES_NEWS,
  GET_COMPANY_NEWS_FROM_ID,
  ICreateCompanyNewsRequest,
  IDeleteCompanyNewsRequest,
  IGetCompanyNewsFromIdRequest,
  IGetCompanyNewsFromIdResponse,
  IUpdateCompanyNewsRequest,
  UPDATE_COMPANIES_NEWS,
} from 'schemas';
import notification from 'services/notificationService';

import { LeftBlock, RightBlock } from './components';
import { DEFAULT_VALUES } from './constants';
import { IFormValues } from './interfaces';
import s from './NewsManager.module.scss';

export const NewsManager: FC = () => {
  const { t } = useContext(LocalizationContext);
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const paramID = params.id === 'new' ? null : params.id;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isDirty },
  } = useForm<IFormValues>({ mode: 'onChange', defaultValues: DEFAULT_VALUES });
  const [linkButton, setLinkButton] = useState<boolean>(false);
  const [isDeleted, setIsDeleted] = useState<boolean>(false);

  const [createNews, { loading: createLoading }] =
    useMutation<ICreateCompanyNewsRequest>(CREATE_COMPANIES_NEWS, {
      fetchPolicy: 'no-cache',
      onCompleted: () => {
        notification.info({
          t,
          type: 'success',
          title: t('create.new.notification.success'),
          dismiss: 5,
        });
        navigate('/news');
      },
      onError: (error: ApolloError) => {
        notification.info({
          t,
          type: 'error',
          title: t('create.new.notification.error'),
          message: error.message,
          dismiss: 5,
        });
      },
    });

  const [deleteNews, { loading: deleteLoading }] =
    useMutation<IDeleteCompanyNewsRequest>(DELETE_COMPANIES_NEWS, {
      fetchPolicy: 'no-cache',
      onCompleted: () => {
        notification.info({
          t,
          type: 'success',
          title: t('delete.new.notification.success'),
          dismiss: 5,
        });
        navigate('/news');
      },
      onError: (error: ApolloError) => {
        notification.info({
          t,
          type: 'error',
          title: t('delete.new.notification.error'),
          message: error.message,
          dismiss: 5,
        });
      },
    });
  const [updateNews, { loading: updateLoading }] =
    useMutation<IUpdateCompanyNewsRequest>(UPDATE_COMPANIES_NEWS, {
      fetchPolicy: 'no-cache',
      onCompleted: () => {
        notification.info({
          t,
          type: 'success',
          title: t('update.new.notification.success'),
          dismiss: 5,
        });
        navigate('/news');
      },
      onError: (error: ApolloError) => {
        notification.info({
          t,
          type: 'error',
          title: t('update.new.notification.error'),
          message: error.message,
          dismiss: 5,
        });
      },
    });

  const { data, loading } = useQuery<
    IGetCompanyNewsFromIdResponse,
    IGetCompanyNewsFromIdRequest
  >(GET_COMPANY_NEWS_FROM_ID, {
    skip: !paramID,
    fetchPolicy: 'network-only',
    variables: paramID ? { companyNewsArticleId: paramID } : undefined,
  });

  useEffect(() => {
    if (data?.companyNewsArticle) {
      const { title, content, image, link, publication_period, active } =
        data.companyNewsArticle;

      setLinkButton(Boolean(link));
      reset({
        title,
        content,
        image,
        link: link ?? { label: '', url: '' },
        publication_period: {
          from: publication_period?.from ?? null,
          until: publication_period?.until ?? null,
        },
        active: !!active,
      });
    }
  }, [data, reset]);

  const handleDelete = (): Promise<FetchResult<unknown>> => {
    return deleteNews({ variables: { deleteCompanyNewsId: paramID } });
  };

  return (
    <Preloader loading={loading}>
      {isDeleted && (
        <ModalDelete
          title={t('modal.delete.news.title')}
          description={t(
            'modal.delete.news.description',
            data?.companyNewsArticle?.title as string
          )}
          loading={deleteLoading}
          onClose={() => setIsDeleted(false)}
          action={handleDelete}
        />
      )}
      <div className={s.container}>
        <div className={s.link_back} onClick={() => navigate('/news')}>
          <BackwardIcon />
          {t('back')}
        </div>

        <div className={s.content}>
          <H2 className={s.title}>
            {paramID
              ? t('newsManager.edit.title')
              : t('newsManager.create.title')}
          </H2>

          <form
            className={s.form}
            onSubmit={handleSubmit(
              ({ title, active, publication_period, content, link, image }) => {
                if (!paramID) {
                  return createNews({
                    variables: {
                      input: {
                        active,
                        publication_period: publication_period?.until
                          ? publication_period
                          : null,
                        content,
                        title,
                        link: linkButton
                          ? { label: link?.label, url: link?.url }
                          : null,
                        image,
                      },
                    },
                  });
                } else {
                  return updateNews({
                    variables: {
                      updateCompanyNewsId: paramID,
                      input: {
                        active,
                        publication_period: publication_period?.until
                          ? publication_period
                          : null,
                        content,
                        title,
                        link: linkButton
                          ? { label: link?.label, url: link?.url }
                          : null,
                        image,
                      },
                    },
                  });
                }
              }
            )}
          >
            <div className={s.form_block}>
              <LeftBlock
                linkButton={linkButton}
                setLinkButton={setLinkButton}
                control={control}
                watch={watch}
                setValue={setValue}
              />

              <RightBlock
                control={control}
                watch={watch}
                linkButton={linkButton}
              />
            </div>

            <div className={cn(s.button_block, paramID && s.double)}>
              {paramID && (
                <Button
                  onClick={() => setIsDeleted(true)}
                  isLoading={deleteLoading}
                  className={s.remove}
                  variant="outline"
                  color="red"
                >
                  {t('delete')}
                </Button>
              )}
              <Button
                isLoading={createLoading || updateLoading}
                type="submit"
                disabled={!isDirty}
                className={s.submit}
              >
                {!paramID ? t('create') : t('save')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Preloader>
  );
};
